const MAX_CONTINUITY_SESSIONS = 3;
const MAX_SUMMARY_LENGTH = 2400;
const MAX_ACTIONS = 4;
const MAX_ACTION_LENGTH = 320;
const MAX_FOLLOW_UP_LENGTH = 640;

function normalizeText(value = "") {
  return String(value || "")
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeRole(entry = {}) {
  const role = normalizeText(entry?.role || entry?.sender || "").toLowerCase();
  return role === "nia" ? "assistant" : role;
}

function messageText(entry = {}) {
  if (typeof entry === "string") return normalizeText(entry);
  return normalizeText(entry?.content || entry?.text || entry?.message || "");
}

function coerceConversation(input) {
  if (Array.isArray(input)) {
    return input
      .map((entry) => ({
        role: normalizeRole(entry),
        text: messageText(entry),
      }))
      .filter((entry) => entry.text);
  }

  if (input && typeof input === "object") {
    const messages = Array.isArray(input.messages) ? coerceConversation(input.messages) : [];
    const explicitLatest = normalizeText(
      input.latestUserMessage || input.text || input.message || input.prompt || ""
    );
    if (explicitLatest && !messages.some((entry, index) =>
      index === messages.length - 1 && entry.role === "user" && entry.text === explicitLatest
    )) {
      messages.push({ role: "user", text: explicitLatest });
    }
    return messages;
  }

  const text = normalizeText(input);
  return text ? [{ role: "user", text }] : [];
}

function latestUserContext(input) {
  const conversation = coerceConversation(input);
  let latestIndex = -1;
  for (let index = conversation.length - 1; index >= 0; index -= 1) {
    if (conversation[index].role === "user" || !conversation[index].role) {
      latestIndex = index;
      break;
    }
  }

  if (latestIndex < 0 && conversation.length) latestIndex = conversation.length - 1;
  return {
    conversation,
    latestText: latestIndex >= 0 ? conversation[latestIndex].text : "",
    priorText: conversation
      .slice(0, Math.max(0, latestIndex))
      .slice(-8)
      .map((entry) => entry.text)
      .filter(Boolean)
      .join(" "),
  };
}

function hasRecordsDomain(text = "") {
  return /\b(summary|summaries|notes?|records?|transcripts?|session history|conversation history|discussion history|discussion notes?|saved (?:session|summary|context|history)|previous (?:session|conversation|discussion)|past (?:session|conversation|discussion)|earlier (?:session|conversation|discussion)|all we converse|our conversations?)\b/i.test(text);
}

function hasAccessDomain(text = "") {
  return /\b(account|log[\s-]?in|logged in|platform|app|coaching machine|previous conversation|past conversation|saved context|history)\b/i.test(text);
}

function hasSessionAccessFailure(text = "") {
  return /\b(?:cannot|can't|could not|couldn't|unable to|not able to)\b[^.!?]{0,80}\b(?:access|open|load|find|retrieve)\b[^.!?]{0,80}\b(?:session|conversation|chat)\b/i.test(text)
    || /\b(?:session|conversation|chat)\b[^.!?]{0,80}\b(?:missing|not available|unavailable|failed to load|won't load|will not load)\b/i.test(text);
}

function hasReminderDomain(text = "") {
  return /\b(reminder|follow[\s-]?up|scheduled date|scheduled time)\b/i.test(text);
}

function hasDeliveryDomain(text = "") {
  return /\b(send|sent|receive|received|deliver|delivered|email(?:ed)?|queue(?:d)?|bounce(?:d)?|suppress(?:ed|ion)?)\b/i.test(text);
}

function hasFailureLanguage(text = "") {
  return /\b(did not|didn't|have not|haven't|has not|hasn't|not reporting|not sent|never sent|not received|never received|missing|failed|failure|wrong|incorrect|inaccurate|odd|cannot access|can't access|no access|could not access|couldn't access|not available|lost|no point)\b/i.test(text);
}

function hasRecordsComplaint(text = "") {
  return sentenceUnits(text).some((unit) => {
    const boundedRecordText = unit.replace(
      /\bsession[-\s]?summary\s+(?:process|workflow|test|testing|verification|generation)\b/gi,
      ""
    );
    return hasRecordsDomain(boundedRecordText) && hasFailureLanguage(boundedRecordText);
  });
}

function hasRecordsDeliveryComplaint(text = "") {
  return sentenceUnits(text).some((unit) => (
    hasRecordsDomain(unit) && hasDeliveryDomain(unit) && hasFailureLanguage(unit)
  ));
}

function hasRecordsRequest(text = "") {
  if (/\bmy agreed action is(?: to)?\b/i.test(text)) return false;
  const qualifiedRecordReference = [
    String.raw`(?:(?:my|our|saved|coach(?:ing)?|session|conversation|discussion|previous|past|earlier|last)\s+){1,2}notes?`,
    String.raw`notes?\s+from\s+(?:(?:my|our|the)\s+)?(?:previous|past|earlier|last|session|conversation|discussion)`,
    String.raw`(?:session|conversation|discussion|previous|past|earlier|last|saved|my|our)\s+(?:summary|summaries|records?|transcripts?)`,
    String.raw`(?:summary|summaries|records?|transcripts?)\s+from\s+(?:(?:my|our|the)\s+)?(?:previous|past|earlier|last|session|conversation|discussion)`,
    String.raw`(?:summary|summaries)\s+of\s+(?:(?:my|our|the)\s+)?[^.!?]{0,40}\b(?:session|conversation|discussion)`,
    String.raw`records?`,
    String.raw`transcripts?`,
  ].join("|");
  const requestVerb = String.raw`(?:send|show|give|retrieve|find|access|provide|keep|save|record|email|share|see|view|download|get|review|copy)`;
  const directRequest = new RegExp(
    String.raw`\b${requestVerb}\b[^.!?]{0,50}\b(?:${qualifiedRecordReference})\b`,
    "i"
  );
  const passiveRequest = new RegExp(
    String.raw`\b(?:${qualifiedRecordReference})\b[^.!?]{0,40}\b(?:sent|shown|provided|shared|emailed|retrieved|available|accessible)\b`,
    "i"
  );
  return directRequest.test(text)
    || passiveRequest.test(text)
    || /\bi (?:would like|want|need) (?:a )?record of\b/i.test(text);
}

function hasEscalationRequest(text = "") {
  return /\b(take|send|escalate|report|raise)\s+(?:this|that|it|the issue|my complaint)\s+(?:to|with)\s+(?:your|a|the|my)?\s*(leader|manager|supervisor|admin|support|coach|human)\b/i.test(text)
    || /\b(i want|let me|need to)\s+(?:speak|talk)\s+(?:to|with)\s+(?:your|a|the)?\s*(leader|manager|supervisor|admin|support|human)\b/i.test(text)
    || /\b(escalation|escalate this|formal complaint)\b/i.test(text)
    || (
      /\b(support review|coach review)\b/i.test(text)
      && /\b(summary|notes?|records?|access|account|platform|session|reminder|delivery|continuity|operational issue)\b/i.test(text)
      && hasFailureLanguage(text)
    );
}

function hasInaccurateRecapSignal(text = "") {
  return /\b(this is not my take|that is not my take|not what i said|not what we discussed|that recap is wrong|that summary is wrong|inaccurate recap|fabricated|made that up|did not say that|didn't say that)\b/i.test(text);
}

function hasContinuitySignal(text = "") {
  return /\b(continue (?:the|our|that) discussion|continue where we left off|pick up where we left off|reconnect|continuity|previous|past|earlier|yesterday|last (?:session|time|week)|we (?:just |already )?(?:had|discussed|talked)|spoke about|raised (?:this|it))\b/i.test(text);
}

function hasExplicitContinuityReentry(text = "") {
  return /\b(?:pick\s+up|continue|resume)\s+(?:right\s+)?where\s+(?:we|i)\s+left\s+off\b|\bwe\s+(?:spoke|talked|discussed)\s+about\b.{0,100}\b(?:yesterday|earlier|before|last\s+(?:time|session|week))\b|\b(?:yesterday|earlier|before|last\s+(?:time|session|week))\b.{0,100}\bwe\s+(?:spoke|talked|discussed)\s+about\b/i.test(text);
}

function hasProductComplaint(text = "") {
  return hasRecordsComplaint(text)
    || hasRecordsRequest(text)
    || sentenceUnits(text).some((unit) => (
      (
        hasSessionAccessFailure(unit)
        || (
          hasAccessDomain(unit)
          && /\b(cannot access|can't access|no access|could not access|couldn't access|cannot log[\s-]?in|can't log[\s-]?in|could not log[\s-]?in|couldn't log[\s-]?in|locked out)\b/i.test(unit)
        )
      )
      || (
        hasReminderDomain(unit)
        && /\b(wrong|incorrect|missing|not received|did not receive|didn't receive|not sent|failed)\b/i.test(unit)
      )
    ));
}

function isUnambiguousExplicitSessionClose(text = "") {
  return /\b(?:we can leave it there|leave it there|that'?s enough for now|enough for now|done for now|we can stop here|let'?s stop there|stop here|(?:please\s+)?complete (?:this|the) session(?: now)?|end the session|close the session|wrap up|no more for now|(?:this|the) session (?:is|feels) complete(?:d)?)\b/i.test(
    normalizeText(text)
  );
}

function hasEstablishedOperationalContext(text = "") {
  return hasProductComplaint(text)
    || hasInaccurateRecapSignal(text)
    || (hasRecordsDomain(text) && (hasContinuitySignal(text) || hasRecordsRequest(text)))
    || (hasEscalationRequest(text) && /\b(summary|notes?|records?|access|platform|session|reminder|delivery)\b/i.test(text));
}

function sentenceUnits(text = "") {
  const normalized = normalizeText(text);
  if (!normalized) return [];
  return normalized.match(/[^.!?]+[.!?]?/g)?.map(normalizeText).filter(Boolean) || [];
}

function isOperationalUnit(text = "", inheritedContext = false) {
  if (!text) return false;
  if (hasEscalationRequest(text)) return true;
  if (hasInaccurateRecapSignal(text) && (inheritedContext || hasRecordsDomain(text))) return true;
  if (hasProductComplaint(text) || hasRecordsRequest(text)) return true;
  if (hasExplicitContinuityReentry(text)) return true;
  if (hasRecordsDomain(text) && hasContinuitySignal(text)) return true;
  return false;
}

function stripTerminalPunctuation(text = "") {
  return normalizeText(text).replace(/[.!?]+$/g, "").trim();
}

function boundedString(value, maxLength) {
  const text = normalizeText(value);
  if (!text) return "";
  if (text.length <= maxLength) return text;
  const bounded = text.slice(0, maxLength + 1);
  const sentenceBoundary = Math.max(
    bounded.lastIndexOf(". "),
    bounded.lastIndexOf("? "),
    bounded.lastIndexOf("! ")
  );
  if (sentenceBoundary >= Math.floor(maxLength * 0.5)) {
    return bounded.slice(0, sentenceBoundary + 1).trim();
  }
  const wordBoundary = bounded.lastIndexOf(" ");
  return bounded.slice(0, wordBoundary > 0 ? wordBoundary : maxLength).trim();
}

function stringArray(value, maxItems = MAX_ACTIONS, maxLength = MAX_ACTION_LENGTH) {
  const values = Array.isArray(value) ? value : (value ? [value] : []);
  return Array.from(new Set(
    values
      .map((entry) => {
        if (typeof entry === "string") return boundedString(entry, maxLength);
        return boundedString(entry?.text || entry?.label || entry?.action || entry?.description || "", maxLength);
      })
      .filter(Boolean)
  )).slice(0, maxItems);
}

function summaryFromSource(source = {}) {
  const candidates = [
    source?.canonicalSummary,
    source?.reviewedSummary,
    source?.summary,
    source?.sessionSummary,
  ].filter((entry) => entry && typeof entry === "object");
  const summaryObject = candidates[0] || {};
  const text = boundedString(
    summaryObject?.summaryText
      || summaryObject?.text
      || summaryObject?.summary
      || source?.summaryText
      || "",
    MAX_SUMMARY_LENGTH
  );
  if (!text) return null;
  return {
    text,
    status: boundedString(
      summaryObject?.status
        || summaryObject?.reviewStatus
        || source?.summaryStatus
        || (source?.reviewed ? "reviewed" : ""),
      48
    ) || "available",
  };
}

function followUpFromSource(source = {}) {
  const candidate = source?.currentFollowUp || source?.followUp || source?.followUpState || null;
  if (!candidate) return null;
  if (typeof candidate === "string") {
    const text = boundedString(candidate, MAX_FOLLOW_UP_LENGTH);
    return text ? { text } : null;
  }
  if (typeof candidate !== "object") return null;
  const text = boundedString(
    candidate?.text
      || candidate?.nextStepText
      || candidate?.recommendation
      || candidate?.nextFollowUpFocus
      || candidate?.focus
      || candidate?.label
      || "",
    MAX_FOLLOW_UP_LENGTH
  );
  const scheduledFor = boundedString(candidate?.scheduledFor || candidate?.dateTime || "", 80);
  const status = boundedString(candidate?.status || "", 48);
  if (!text && !scheduledFor) return null;
  return {
    ...(text ? { text } : {}),
    ...(scheduledFor ? { scheduledFor } : {}),
    ...(status ? { status } : {}),
  };
}

function sanitizeContinuitySession(source = {}, identity = {}) {
  if (!source || typeof source !== "object") return null;
  if (source.orgId != null && normalizeText(source.orgId) !== identity.orgId) return null;
  if (source.userId != null && normalizeText(source.userId) !== identity.userId) return null;

  const summary = summaryFromSource(source);
  const agreedActions = stringArray(source?.agreedActions || source?.actions || source?.actionItems);
  const followUp = followUpFromSource(source);
  if (!summary && !agreedActions.length && !followUp) return null;

  return {
    sourceSessionId: boundedString(source?.sourceSessionId || source?.sessionId || source?.id || "", 160),
    title: boundedString(source?.title || source?.topic || "", 180),
    ...(summary ? { summary } : {}),
    agreedActions,
    ...(followUp ? { followUp } : {}),
  };
}

function sanitizeValidatedContinuityPacket(packet, identity = {}) {
  if (!packet || typeof packet !== "object") return null;
  const expectedOrgId = normalizeText(identity?.orgId);
  const expectedUserId = normalizeText(identity?.userId);
  const packetOrgId = normalizeText(packet?.orgId);
  const packetUserId = normalizeText(packet?.userId);
  if (!expectedOrgId || !expectedUserId || packetOrgId !== expectedOrgId || packetUserId !== expectedUserId) {
    return null;
  }

  const suppliedSessions = [
    ...(Array.isArray(packet?.relevantSessions) ? packet.relevantSessions : []),
    ...(Array.isArray(packet?.sessions) ? packet.sessions : []),
  ];
  const sources = suppliedSessions.length ? suppliedSessions : [packet];
  const sessions = sources
    .map((source) => sanitizeContinuitySession(source, {
      orgId: expectedOrgId,
      userId: expectedUserId,
    }))
    .filter(Boolean)
    .slice(0, MAX_CONTINUITY_SESSIONS);
  if (!sessions.length) return null;

  return {
    orgId: expectedOrgId,
    userId: expectedUserId,
    sessions,
  };
}

function buildValidatedContinuityContext(packet, identity = {}) {
  const sanitizedPacket = sanitizeValidatedContinuityPacket(packet, identity);
  if (!sanitizedPacket) return null;
  const session = sanitizedPacket.sessions[0];
  const sections = [];
  if (session.title) sections.push(`Topic: ${session.title}`);
  if (session.summary?.text) sections.push(`Saved summary: ${session.summary.text}`);
  if (session.agreedActions.length) sections.push(`Agreed actions: ${session.agreedActions.join("; ")}`);
  if (session.followUp?.text) sections.push(`Current follow-up: ${session.followUp.text}`);
  if (session.followUp?.scheduledFor) sections.push(`Scheduled for: ${session.followUp.scheduledFor}`);
  return {
    packet: sanitizedPacket,
    sourceSessionId: session.sourceSessionId || "",
    summaryText: session.summary?.text || "",
    summaryStatus: session.summary?.status || "",
    agreedActions: [...session.agreedActions],
    followUp: session.followUp || null,
    contextText: sections.join("\n"),
  };
}

function classifyOperationalIntent(input, options = {}) {
  const { latestText, priorText } = latestUserContext(input);
  const explicitEstablishedContext = Boolean(options?.establishedContext);
  const establishedContext = explicitEstablishedContext || hasEstablishedOperationalContext(priorText);
  const escalationRequest = hasEscalationRequest(latestText);
  const inaccurateRecap = hasInaccurateRecapSignal(latestText)
    && (establishedContext || hasRecordsDomain(latestText) || hasContinuitySignal(latestText));
  const missingSummary = hasRecordsComplaint(latestText);
  const continuityRequest = hasExplicitContinuityReentry(latestText)
    || (
      hasContinuitySignal(latestText)
      && (hasRecordsDomain(latestText) || hasAccessDomain(latestText))
    );
  const recordRequest = hasRecordsRequest(latestText);
  const reminderComplaint = hasReminderDomain(latestText)
    && /\b(wrong|incorrect|missing|not received|did not receive|didn't receive|not sent|failed)\b/i.test(latestText);
  const deliveryComplaint = hasRecordsDeliveryComplaint(latestText)
    || sentenceUnits(latestText).some((unit) => (
      hasReminderDomain(unit) && hasDeliveryDomain(unit) && hasFailureLanguage(unit)
    ));
  const complaint = hasProductComplaint(latestText) || inaccurateRecap;
  const operational = complaint
    || escalationRequest
    || recordRequest
    || continuityRequest
    || (inaccurateRecap && establishedContext);

  const units = sentenceUnits(latestText);
  const residualUnits = operational
    ? units.filter((unit) => !isOperationalUnit(unit, establishedContext))
    : units;
  const residualText = normalizeText(residualUnits.join(" "));
  const hasCoachingTopic = Boolean(
    operational
    && residualText
    && !/^(thanks?|thank you|okay|ok|please|fine|that is all|that's all)[.!?]*$/i.test(residualText)
  );
  const pureOperational = Boolean(operational && !hasCoachingTopic);

  return {
    operational,
    complaint,
    escalationRequest,
    missingSummary,
    continuityRequest,
    recordRequest,
    reminderComplaint,
    deliveryComplaint,
    inaccurateRecap,
    establishedContext,
    pureOperational,
    hasCoachingTopic,
    residualText,
    latestText,
    reason: !operational
      ? "No bounded operational, records, access, delivery, or escalation intent was found."
      : escalationRequest && !establishedContext && !complaint
        ? "An escalation was requested without an established complaint in the available context."
        : complaint
          ? "A product, records, continuity, reminder, or delivery complaint is active."
          : "A bounded records, continuity, or support request is active.",
  };
}

const CONVERSATION_REPAIR_ACKNOWLEDGEMENT =
  "You're right—I responded to the reporting words instead of your actual objective.";
const GENERAL_CONVERSATION_REPAIR_ACKNOWLEDGEMENT =
  "You're right—I responded to something other than your actual objective.";

function hasConversationRepairComplaint(text = "") {
  return /\b(i (?:did not|didn't) ask (?:for|about) (?:that|this)|that(?:'s| is) not what i asked|you(?:'re| are) not listening(?: to me)?(?: again)?|you did not listen|you didn't listen)\b/i.test(text);
}

function extractStatedObjective(conversation = [], latestIndex = conversation.length) {
  for (let index = latestIndex - 1; index >= 0; index -= 1) {
    const entry = conversation[index];
    if (entry.role !== "user") continue;
    const text = normalizeText(entry.text);
    if (!text || hasConversationRepairComplaint(text)) continue;
    const agreedAction = text.match(/\bmy agreed action is(?: to)?\s+([^.!?]+)/i);
    if (agreedAction?.[1]) return `to ${stripTerminalPunctuation(agreedAction[1])}`;
    const objective = text.match(/\b(?:i want|i need|i would like|i'd like|my (?:goal|objective) is)\s+to\s+([^.!?]+)/i);
    if (objective?.[1]) return `to ${stripTerminalPunctuation(objective[1])}`;
  }
  return "";
}

function classifyConversationRepair(input) {
  const conversation = coerceConversation(input);
  let latestIndex = -1;
  for (let index = conversation.length - 1; index >= 0; index -= 1) {
    if (conversation[index].role === "user") {
      latestIndex = index;
      break;
    }
  }
  const latestText = latestIndex >= 0 ? conversation[latestIndex].text : "";
  const repair = hasConversationRepairComplaint(latestText)
    && conversation.slice(0, latestIndex).some((entry) => entry.role === "assistant");
  const priorAssistantText = conversation
    .slice(0, latestIndex)
    .filter((entry) => entry.role === "assistant")
    .map((entry) => entry.text)
    .join(" ");
  const reportingMisroute = /\b(missing-summary|continuity issue|saved context|coach\/support review|records? or continuity)\b/i.test(priorAssistantText);
  const acknowledgementAlreadyGiven = conversation
    .slice(0, latestIndex)
    .some((entry) => (
      entry.role === "assistant"
      && (
        entry.text.includes(CONVERSATION_REPAIR_ACKNOWLEDGEMENT)
        || entry.text.includes(GENERAL_CONVERSATION_REPAIR_ACKNOWLEDGEMENT)
      )
    ));
  return {
    repair,
    latestText,
    objective: repair ? extractStatedObjective(conversation, latestIndex) : "",
    reportingMisroute,
    acknowledgementRequired: Boolean(repair && !acknowledgementAlreadyGiven),
  };
}

function buildConversationRepairResponse(repairInput) {
  const repair = repairInput && typeof repairInput === "object" && "repair" in repairInput
    ? repairInput
    : classifyConversationRepair(repairInput);
  if (!repair.repair) return "";
  const lines = [];
  if (repair.acknowledgementRequired) {
    lines.push(
      repair.reportingMisroute
        ? CONVERSATION_REPAIR_ACKNOWLEDGEMENT
        : GENERAL_CONVERSATION_REPAIR_ACKNOWLEDGEMENT
    );
  }
  if (repair.objective) {
    lines.push(`Your stated objective is ${repair.objective}.`);
    lines.push("Let's return to that: what is the one result you need to record before you stop for today?");
  } else {
    lines.push("I will return to what you actually asked, without treating this correction as a coaching block. What specific part should I address now?");
  }
  return lines.join(" ");
}

function classifyCoachingServiceStatusRequest(input) {
  const { conversation, latestText } = latestUserContext(input);
  const statusRequest = /\b(?:are you|you are|you(?:'re| are) not|not)\s+(?:still\s+)?connected to (?:the )?(?:coaching )?(?:engine|service)|\b(?:coaching )?(?:engine|service)\s+(?:connected|connection|online|working|available)\b/i.test(latestText);
  const priorAssistantText = conversation
    .filter((entry) => entry.role === "assistant")
    .map((entry) => entry.text)
    .join(" ");
  const reference = priorAssistantText.match(/\bReference:\s*([0-9a-f]{8}-[0-9a-f-]{27,})\b/i)?.[1] || "";
  return {
    statusRequest,
    reference,
    priorServiceFailure: /\bcoaching service (?:is unavailable|request failed)|\bdegraded service fallback\b/i.test(priorAssistantText),
    priorReconnectClaim: /\breconnect(?:ing)? to the coaching engine\b/i.test(priorAssistantText),
  };
}

function buildCoachingServiceStatusResponse(statusInput) {
  const status = statusInput && typeof statusInput === "object" && "statusRequest" in statusInput
    ? statusInput
    : classifyCoachingServiceStatusRequest(statusInput);
  if (!status.statusRequest) return "";
  if (status.priorServiceFailure || status.priorReconnectClaim) {
    const correction = status.priorReconnectClaim
      ? "The earlier reconnect wording was inaccurate. "
      : "";
    const reference = status.reference ? ` Reference: ${status.reference}.` : "";
    return `${correction}The prior response was a degraded service fallback, not a coaching response. No reconnect operation is in progress.${reference}`;
  }
  return "I use a coaching service to generate coaching responses, but this status question cannot prove a persistent live connection. No reconnect operation is in progress.";
}

function applyCanonicalOperationalIntent(record = {}, input) {
  const canonical = input && typeof input === "object" && "operational" in input
    ? input
    : classifyOperationalIntent(input);
  const sessionSummary = record?.sessionSummary && typeof record.sessionSummary === "object"
    ? record.sessionSummary
    : {};
  const summaryCoachingState = sessionSummary?.coachingState && typeof sessionSummary.coachingState === "object"
    ? sessionSummary.coachingState
    : {};
  return {
    ...record,
    operationalIntent: canonical,
    sessionSummary: {
      ...sessionSummary,
      operationalIntent: canonical,
      coachingState: {
        ...summaryCoachingState,
        operationalIntent: canonical,
      },
    },
  };
}

function classifyFactualPlanningIntent(input) {
  const { latestText } = latestUserContext(input);
  const retirementSignals = [
    /\bown means\b/i,
    /\bpension(?: income)?\b/i,
    /\bliving annuit(?:y|ies)\b/i,
    /\bannuit(?:y|ies)\b/i,
    /\bretirement\b/i,
    /\bwithdrawal rate\b/i,
    /\bmonthly income\b/i,
    /\bpersonal funds?\b/i,
    /\binflation\b/i,
    /\btax implications?\b/i,
  ].filter((pattern) => pattern.test(latestText)).map((pattern) => pattern.source);
  const strongCoachingSignal = /\b(i feel|i felt|i'm feeling|i am feeling|overwhelmed|panicked|anxious|stuck|hopeless|afraid|terrified|avoid(?:ing)?|procrastinat(?:e|ing)|no point trying|always fail|cannot cope|can't cope)\b/i.test(latestText);
  const retirementPlanning = retirementSignals.length >= 2 && !strongCoachingSignal;
  return {
    factualPlanning: retirementPlanning,
    retirementPlanning,
    latestText,
    evidence: retirementSignals,
  };
}

function quoteGroundedText(text = "") {
  const cleaned = normalizeText(text);
  return cleaned ? `“${cleaned}”` : "";
}

function buildMixedTopicContinuation(residualText = "") {
  const topic = stripTerminalPunctuation(residualText);
  if (!topic) return "";
  const quotedTopic = quoteGroundedText(topic);
  if (
    /\bfaith\b/i.test(topic) &&
    /\bintellectual\b/i.test(topic) &&
    /\bpersonal\b/i.test(topic)
  ) {
    return [
      `On the separate faith concern you raised — ${quotedTopic} — an intellectual understanding and a personal sense of connection can develop at different speeds.`,
      "The absence of a strong feeling does not by itself mean that your faith is insincere or that you are doing something wrong. Notice one recent moment when faith felt most personal or most distant, and what was happening then.",
    ].join(" ");
  }
  return `On the separate topic you raised — ${quotedTopic} — we can continue from that point without treating the complaint itself as a coaching block.`;
}

function buildOperationalSupportResponse(intentInput, options = {}) {
  const intent = intentInput && typeof intentInput === "object" && "operational" in intentInput
    ? intentInput
    : classifyOperationalIntent(intentInput, options);
  if (!intent.operational) return "";

  const identity = options?.identity || {
    orgId: options?.orgId,
    userId: options?.userId,
  };
  const continuity = buildValidatedContinuityContext(options?.continuityPacket, identity);
  const lines = [];

  if (intent.inaccurateRecap) {
    lines.push("You're right to reject that recap. It should not be treated as an accurate record of your earlier discussion.");
  } else if (intent.escalationRequest && intent.establishedContext) {
    lines.push("I hear the escalation. The continuity or notes failure you raised should be reviewed as an operational issue, not analysed as a coaching pattern.");
  } else if (intent.escalationRequest) {
    lines.push("I'll treat that as an escalation request, not as a coaching pattern.");
  } else if (intent.missingSummary || intent.deliveryComplaint) {
    lines.push("I'm sorry the notes or summary you expected were not available here. That continuity failure should be acknowledged before we continue.");
  } else if (intent.reminderComplaint) {
    lines.push("I understand that the reminder details are wrong. That is an operational issue, not a limiting belief.");
  } else {
    lines.push("I understand the records or continuity issue you've raised, and I'll treat it as an operational request.");
  }

  lines.push("I can use available saved context and summaries linked to your Nia COACH account, but I may not be able to retrieve or send every past transcript directly from this chat.");

  const hasContinuityEvidence = Boolean(
    continuity &&
    (
      continuity.summaryText ||
      continuity.agreedActions.length ||
      continuity.followUp?.text ||
      continuity.followUp?.scheduledFor
    )
  );
  if (hasContinuityEvidence) {
    if (continuity.summaryText) {
      lines.push(`The matching saved summary available to this request says: ${quoteGroundedText(continuity.summaryText)}`);
    }
    if (continuity.agreedActions.length) {
      lines.push(`The saved agreed action${continuity.agreedActions.length === 1 ? " is" : "s are"}: ${continuity.agreedActions.join("; ")}.`);
    }
    if (continuity.followUp?.text) {
      lines.push(`The saved current follow-up is: ${continuity.followUp.text}`);
    }
    if (continuity.followUp?.scheduledFor) {
      lines.push(`The saved follow-up is scheduled for: ${continuity.followUp.scheduledFor}`);
    }
  } else if (intent.escalationRequest && !intent.establishedContext && !intent.complaint) {
    lines.push("The issue to escalate is not established in the available conversation context, so I will not guess what happened. Please name the operational issue you want reviewed.");
  } else {
    lines.push("I could not safely match relevant saved context in this request, so I will not invent a recap.");
  }

  lines.push("The missing-summary or continuity issue should go to coach/support review; I will not claim that notes were sent, restored, fixed, or delivered without evidence.");

  if (intent.hasCoachingTopic && intent.residualText) {
    lines.push(buildMixedTopicContinuation(intent.residualText));
  }

  return lines.join("\n\n");
}

module.exports = {
  MAX_CONTINUITY_SESSIONS,
  CONVERSATION_REPAIR_ACKNOWLEDGEMENT,
  applyCanonicalOperationalIntent,
  buildCoachingServiceStatusResponse,
  buildConversationRepairResponse,
  buildOperationalSupportResponse,
  buildValidatedContinuityContext,
  classifyCoachingServiceStatusRequest,
  classifyConversationRepair,
  classifyFactualPlanningIntent,
  classifyOperationalIntent,
  isUnambiguousExplicitSessionClose,
  sanitizeValidatedContinuityPacket,
};
